import { useEffect, useState } from 'react';
import api, { ReviewCampaign, ReviewItem } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { errMsg } from '../utils';

export default function AccessReviewsPage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('iga-admin');
  const canReview = hasRole('iga-admin', 'iga-reviewer');

  const [campaigns, setCampaigns] = useState<ReviewCampaign[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const loadCampaigns = async () => {
    setError('');
    try {
      const r = await api.get<ReviewCampaign[]>('/access-reviews/campaigns');
      setCampaigns(r.data);
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const loadItems = async (id: string) => {
    setSelected(id);
    try {
      const r = await api.get<ReviewItem[]>(`/access-reviews/campaigns/${id}/items`);
      setItems(r.data);
    } catch (e) {
      setError(errMsg(e));
    }
  };

  useEffect(() => {
    void loadCampaigns();
  }, []);

  const createCampaign = async () => {
    setError('');
    try {
      await api.post('/access-reviews/campaigns', { name });
      setName('');
      await loadCampaigns();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const decide = async (itemId: string, decision: 'certify' | 'revoke') => {
    if (!selected) return;
    try {
      await api.post(`/access-reviews/campaigns/${selected}/items/${itemId}/decision`, { decision });
      await loadItems(selected);
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const complete = async () => {
    if (!selected) return;
    try {
      await api.post(`/access-reviews/campaigns/${selected}/complete`, {});
      await loadCampaigns();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-white">Access-Reviews</h2>
      {error && <p className="text-coral">{error}</p>}

      {isAdmin && (
        <section className="card flex items-end gap-3">
          <div className="flex-1">
            <label className="label">Neue Kampagne</label>
            <input
              className="input"
              placeholder="Q2 2026 Rezertifizierung"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <button className="btn-primary" disabled={!name} onClick={createCampaign}>
            Starten
          </button>
        </section>
      )}

      <div className="grid grid-cols-3 gap-6">
        <section className="card col-span-1 space-y-2">
          <h3 className="font-medium text-coral">Kampagnen</h3>
          {campaigns.map((c) => (
            <button
              key={c.id}
              onClick={() => loadItems(c.id)}
              className={`block w-full rounded-md px-3 py-2 text-left text-sm ${
                selected === c.id ? 'bg-coral text-black' : 'hover:bg-ink'
              }`}
            >
              <div className="font-medium">{c.name}</div>
              <div className="text-xs opacity-70">{c.status}</div>
            </button>
          ))}
          {campaigns.length === 0 && <p className="text-sm text-gray-500">Keine Kampagnen.</p>}
        </section>

        <section className="card col-span-2 overflow-x-auto">
          {!selected ? (
            <p className="text-sm text-gray-500">Kampagne wählen.</p>
          ) : (
            <>
              <div className="mb-3 flex justify-end">
                {isAdmin && (
                  <button className="btn-ghost" onClick={complete}>
                    Kampagne abschließen
                  </button>
                )}
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-edge">
                    <th className="th">User</th>
                    <th className="th">Typ</th>
                    <th className="th">Berechtigung</th>
                    <th className="th">Entscheidung</th>
                    <th className="th"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="border-b border-edge/50">
                      <td className="td">{it.username}</td>
                      <td className="td">{it.kind}</td>
                      <td className="td">{it.name}</td>
                      <td className="td">{it.decision}</td>
                      <td className="td">
                        {canReview && it.decision === 'pending' && (
                          <div className="flex gap-2">
                            <button className="btn-primary" onClick={() => decide(it.id, 'certify')}>
                              Bestätigen
                            </button>
                            <button className="btn-ghost" onClick={() => decide(it.id, 'revoke')}>
                              Entziehen
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td className="td text-gray-500" colSpan={5}>
                        Keine Items.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
